<template>
  <header
    v-if="isShow"
    class="ni-nav-bar"
    :class="[
      tone === 'dark' ? 'ni-nav-bar--dark' : 'ni-nav-bar--light',
      solid ? 'ni-nav-bar--solid' : '',
      mobileMenuOpen ? 'is-menu-open' : '',
    ]"
  >
    <div class="ni-nav-bar__inner">
      <a href="/" class="ni-nav-bar__logo" @click.prevent="goHome">
        <img v-if="logoSrc" :src="logoSrc" alt="HI QINGDAO" />
      </a>

      <nav class="ni-nav-bar__nav" aria-label="主导航">
        <div v-for="item in navItems" :key="item.id" class="ni-nav-bar__item">
          <a
            :href="item.href"
            class="ni-nav-bar__link"
            :class="{ 'is-active': isActive(item) }"
            @click="handleMenuClick(item, $event)"
          >
            <span>{{ item.label }}</span>
            <i
              v-if="item.children.length"
              class="iconfont icon-2701jiantou3 ni-nav-bar__arrow"
              aria-hidden="true"
            />
          </a>
          <div v-if="item.children.length" class="ni-nav-bar__submenu">
            <a
              v-for="child in item.children"
              :key="child.id"
              :href="child.href"
              class="ni-nav-bar__sublink"
              :target="child.target === '_blank' ? '_blank' : undefined"
              :rel="child.target === '_blank' ? 'noopener noreferrer' : undefined"
              @click="handleMenuClick(child, $event)"
            >
              {{ child.label }}
            </a>
          </div>
        </div>
      </nav>

      <div class="ni-nav-bar__side">
        <div class="ni-nav-bar__lang">
          <NewIndexChangeLang :show-icon="false" :tone="tone === 'dark' ? 'dark' : 'light'" />
        </div>
        <div v-if="!isLoggedIn" class="ni-nav-bar__login" @click="openLogin">
          <button type="button" class="ni-nav-bar__login-icon" :title="t('loginOrRegister')">
            <i class="iconfont icon-icontouxiang" aria-hidden="true" />
          </button>
          <span class="ni-nav-bar__login-text">{{ t('loginOrRegister') }}</span>
        </div>
        <div v-else class="ni-nav-bar__login ni-nav-bar__user">
          <!-- 与首页一致：图标旁展示用户名/脱敏手机号 -->
          <button type="button" class="ni-nav-bar__login-icon" :title="username" @click="toUserHome">
            <i class="iconfont icon-icontouxiang" aria-hidden="true" />{{ username }}
          </button>
          <span class="ni-nav-bar__login-text" role="button" tabindex="0" @click="toUserHome">
            <i class="iconfont icon-icontouxiang" aria-hidden="true" />{{ username }}
          </span>
          <div class="ni-nav-bar__user-menu">
            <button
              v-for="item in userMenuItems"
              :key="item.id"
              type="button"
              @click.stop="handleUserMenu(item)"
            >
              {{ item.name }}
            </button>
          </div>
        </div>
        <button
          type="button"
          class="ni-nav-bar__burger"
          :aria-label="mobileMenuOpen ? 'close' : 'menu'"
          :aria-expanded="mobileMenuOpen"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <i class="iconfont" :class="mobileMenuOpen ? 'icon-chahao2' : 'icon-listo'" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div v-if="mobileMenuOpen" class="ni-nav-bar__drawer-root">
      <div class="ni-nav-bar__drawer-mask" @click="mobileMenuOpen = false" />
      <div class="ni-nav-bar__drawer" role="dialog" aria-modal="true" aria-label="菜单">
        <div class="ni-nav-bar__drawer-head">
          <button
            type="button"
            class="ni-nav-bar__drawer-close"
            aria-label="close"
            @click="mobileMenuOpen = false"
          >
            ×
          </button>
        </div>
        <div class="ni-nav-bar__drawer-body">
          <div v-for="item in navItems" :key="`m-${item.id}`" class="ni-nav-bar__drawer-item">
            <a :href="item.href" @click="closeMobileMenu(item, $event)">{{ item.label }}</a>
            <div v-if="item.children.length" class="ni-nav-bar__drawer-sub">
              <a
                v-for="child in item.children"
                :key="`m-${child.id}`"
                :href="child.href"
                :target="child.target === '_blank' ? '_blank' : undefined"
                @click="closeMobileMenu(child, $event)"
              >
                {{ child.label }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Login2
      :show-dialog="showDialog"
      :show-login="showLogin"
      :show-mobile="showMobile"
      @update:show-dialog="onLoginDialogChange"
    />
  </header>
</template>

<script setup>
import { computed, getCurrentInstance, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import NewIndexChangeLang from './NewIndexChangeLang.vue';
import Login2 from '@/components/common/login2.vue';
import { usePortalNav } from '@/composables/usePortalNav.js';
import { isExpandOnly, navHref, navLabel } from '@/utils/newIndexNav.js';
import { formatDisplayName } from '@/utils/phoneVerify.js';

const props = defineProps({
  solid: { type: Boolean, default: false },
  /** HeaderVR 等场景：忽略页面 provide 的 showHeader，始终展示 */
  forceVisible: { type: Boolean, default: false },
  /** light=白字（压在轮播上）；dark=深字（浅色底）；不传则跟 logoColor */
  tone: { type: String, default: '' },
});

const { proxy } = getCurrentInstance();
const router = useRouter();
const route = useRoute();
const store = useStore();
const { t, locale } = useI18n();
const { navTree, ensureNav } = usePortalNav();

const logoColor = inject('logoColor', 'white');
const injectedShow = inject('showHeader', true);
const isShow = computed(() => (props.forceVisible ? true : injectedShow));
const mobileMenuOpen = ref(false);
const showDialog = ref(false);
const showLogin = ref(false);
const showMobile = ref(false);
const isLoggedIn = ref(false);
const username = ref(t('anonymity'));
const logoUrl = ref('');

const tone = computed(() => {
  if (props.tone === 'light' || props.tone === 'dark') return props.tone;
  return logoColor === 'black' || logoColor === 'red' ? 'dark' : 'light';
});

const logoSrc = computed(() => {
  const info = store.state.common.siteInfo || {};
  if (logoUrl.value) return logoUrl.value;
  if (tone.value === 'light') return info.logo_white || info.logo || '';
  if (logoColor === 'red') return info.logo_red || info.logo || '';
  return info.logo || info.logo_white || '';
});

const userMenuItems = computed(() => [
  { id: 1, name: t('personCenter'), type: 'user' },
  { id: 3, name: t('sideMenu.notice'), type: 'notice' },
  { id: 2, name: t('logout'), type: 'exit' },
]);

const mapNavItem = (item) => ({
  ...item,
  label: navLabel(item, locale.value),
  href: navHref(item),
  anchor: item.type === 'anchor' ? item.value : '',
  target: item.target || '_self',
  children: (item.children || []).map((child) => ({
    ...child,
    label: navLabel(child, locale.value),
    href: navHref(child),
    target: child.target || '_self',
  })),
});

const navItems = computed(() => (navTree.value || []).map(mapNavItem));

function isActive(item) {
  const href = item?.href || '';
  if (!href || href === '#') return false;
  if (href === '/' || href === '/oldIndex') {
    return route.path === '/' || route.path === '/newIndex';
  }
  return route.path === href || route.path.startsWith(`${href}/`);
}

function goHome() {
  router.push('/');
}

function openLogin() {
  showDialog.value = true;
  showLogin.value = true;
  showMobile.value = false;
}

function onLoginDialogChange(value) {
  showDialog.value = value?.value ?? value;
}

function toUserHome() {
  router.push('/my/home');
}

function handleUserMenu(item) {
  if (item.type === 'exit') {
    proxy.$cookies.removeToken();
    proxy.$cookies.removeUsername();
    proxy.$cookies.removeMobile();
    proxy.$cookies.removeTokentype();
    proxy.$cookies.removeAuthKey();
    location.reload();
    return;
  }
  if (item.type === 'user') router.push('/my/setting');
  if (item.type === 'notice') router.push('/my/notice');
}

function handleMenuClick(item, event) {
  if (isExpandOnly(item)) {
    event?.preventDefault?.();
    return;
  }
  const href = item?.href || '';
  if (item?.target === '_blank' && href && href !== '#') {
    event?.preventDefault?.();
    window.open(href, '_blank', 'noopener,noreferrer');
    return;
  }
  event?.preventDefault?.();
  if (!href || href === '#') return;
  if (/^https?:\/\//i.test(href)) {
    window.location.href = href;
    return;
  }
  mobileMenuOpen.value = false;
  router.push(href);
}

function closeMobileMenu(item, event) {
  mobileMenuOpen.value = false;
  handleMenuClick(item, event || { preventDefault() {} });
}

onMounted(async () => {
  await ensureNav();
  const info = store.state.common.siteInfo;
  if (!info || !Object.keys(info).length) {
    store.dispatch('common/fetchSiteInfo').then((res) => {
      logoUrl.value = '';
      store.state.common.siteInfo = res;
    }).catch(() => {});
  }
  const isAuthenticated = proxy.$cookies.getAuthKey();
  if (
    isAuthenticated &&
    proxy.$cookies.getToken() !== undefined &&
    proxy.$cookies.getToken() !== '' &&
    proxy.$cookies.getToken() != null
  ) {
    isLoggedIn.value = true;
    username.value = formatDisplayName(
      store.state.user.userInfo.username,
      store.state.user.userInfo.mobile,
    );
  }
});
</script>

<style src="@/assets/css/new_index_nav_bar.css"></style>
